/**
 * #754 — the overview search control, above the toolbar's own rows.
 *
 * Presentational by contract: it holds the draft text the trader is typing and
 * nothing else. Committing, clearing and the whole selection live with the
 * board, so this control can never narrow a view on its own, and never has an
 * executable target to lose.
 *
 * It deliberately renders NO Today / This Week / return-to-Manual affordance.
 * Those are #902's `DiscoveryModeControls`, mounted directly below this bar,
 * and a second Today beside the toolbar's existing one is the exact confusion
 * #754 forbids.
 *
 * While one of those modes is on, that component states the dates, the counts
 * and the way to the rest, because they are facts about the mode's own window.
 * What stays here is the half a mode does not change: the query, and the
 * filters still applying underneath it. `counts: null` and `remaining: 0` are
 * how the board says "the controls below are speaking for this".
 */
import { useState } from 'react';
import { SearchInput } from '@/components/molecules';
import type { DiscoveryCoverage } from './discoverySearch';

interface DiscoverySearchBarProps {
  /** The COMMITTED query. Drives the draft only when it changes underneath. */
  query: string;
  onCommit: (query: string) => void;
  onClear: () => void;
  /** Present only while a query is committed. */
  coverage: DiscoveryCoverage | null;
  /** Eligible groups beyond the ones on screen. Zero while a mode owns the
   *  page, which is also how "Show all" moves to its `onShowMore`. */
  remaining: number;
  onShowAll: () => void;
  /** Futures have no date; keep the advertised filter recovery reachable. */
  onUseAllDates?: () => void;
  /** Set when this board cannot be searched at all, e.g. Spread/Total. */
  unsupportedNote?: string | null;
}

export function DiscoverySearchBar({
  query, onCommit, onClear, coverage, remaining, onShowAll, onUseAllDates, unsupportedNote,
}: DiscoverySearchBarProps) {
  const [draft, setDraft] = useState(query);
  // The committed query can move without this control: a Back onto an earlier
  // board resumes one, Reset drops one, and an explicit sport or date gesture
  // ends the search. Following it keeps the box showing what the board is
  // actually filtered by — while ordinary typing, which never changes `query`,
  // is left completely alone.
  //
  // Adjusted during render rather than in an effect (React's own "derive state
  // from props" pattern): the corrected box is what paints, so the trader never
  // sees one frame of the previous query.
  const [lastCommitted, setLastCommitted] = useState(query);
  if (lastCommitted !== query) {
    setLastCommitted(query);
    setDraft(query);
  }

  if (unsupportedNote) {
    return (
      <p data-testid="discovery-unsupported" className="text-muted-foreground mb-2 text-xs">
        {unsupportedNote}
      </p>
    );
  }

  return (
    <div className="mb-3 min-w-0 max-w-xl flex-[1_1_20rem]">
      <SearchInput
        size="sm"
        value={draft}
        onChange={(next) => {
          setDraft(next);
          // The clear button empties the draft; an empty box means the trader
          // is done searching, so the board returns to its ordinary view
          // without waiting for an Enter they have no reason to press.
          if (next === '') onClear();
        }}
        onSubmit={(next) => onCommit(next)}
        onEscape={() => {
          // Escape ends the search whether or not it was ever committed: an
          // abandoned half-typed word left in the box reads as a filter that
          // is still in force.
          setDraft('');
          onClear();
        }}
        inputLabel="Search markets"
        clearLabel="Clear search"
        placeholder="Search teams, events or tickers — press Enter"
        className="max-w-xl"
      />
      {coverage && (
        <div
          data-testid="discovery-scope"
          role="status"
          className="text-muted-foreground mt-1.5 space-y-0.5 break-words text-xs [overflow-wrap:anywhere]"
        >
          <p>
            <span>{coverage.scope}</span>
            {coverage.counts !== null && (
              <>
                <span aria-hidden="true"> · </span>
                <span className="text-foreground">{coverage.counts}</span>
              </>
            )}
            {remaining > 0 && (
              <>
                <span aria-hidden="true"> · </span>
                <button
                  type="button"
                  onClick={onShowAll}
                  className="text-primary underline-offset-2 hover:underline"
                >
                  Show all {remaining} more
                </button>
              </>
            )}
          </p>
          {coverage.gaps.map((gap) => <p key={gap}>{gap}</p>)}
          {onUseAllDates && (
            <button type="button" onClick={onUseAllDates}
              className="text-primary underline-offset-2 hover:underline">
              Use all dates for this search
            </button>
          )}
        </div>
      )}
    </div>
  );
}
